import { ChangeDetectionStrategy, Component, OnDestroy, OnInit, inject } from '@angular/core'
import { AsyncPipe } from '@angular/common'
import { Actions, ofType } from '@ngrx/effects'
import { select, Store } from '@ngrx/store'
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs'
import {
  distinctUntilChanged,
  filter,
  map,
  shareReplay,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  withLatestFrom,
} from 'rxjs/operators'
import { BackendService } from '../../../api/backend/backend.service'
import {
  AccountOpportunityFilterOptionsDto,
  AccountOpportunityListItemDto,
  AccountOpportunityPageDto,
  ProgramInitiationAccountMappingDto,
} from '../../../api/generated/api.generated'
import { CarrierData } from '../../../api/model/backend.model'
import { ApiResponse, MaybeData, MaybeError } from '../../../api/model/api.model'
import { AppState } from '../../../core/store'
import { selectIsSaving } from '../../../core/store/accountopportunity.selectors'
import { selectCurrentClientID } from '../../../core/store/broker/broker.selectors'
import { selectClientsByID } from '../../../core/store/clients.selectors'
import { ThemeContainerComponent } from '../../../shared/theme.container'
import { equals } from 'ramda'
import {
  AccountMappingData,
  AccountOpportunityFilters,
  AccountOpportunityQuery,
  DEFAULT_ACCOUNT_OPPORTUNITY_PAGE_SIZE,
  EMPTY_ACCOUNT_OPPORTUNITY_FILTERS,
  earliestMappedAccount,
  hasActiveFilters,
  MIN_SEARCH_LENGTH,
} from '../../model/program-initiation.model'
import { ProgramInitiationFlowService } from '../../services/program-initiation-flow.service'
import * as fromProgramInitiation from '../../store/program-initation.actions'
import { ProgramInitiationComponent } from '../program-initiation/program-initiation.component'
import {
  ProgramInitiationPageChange,
  ProgramInitiationSortChange,
} from '../program-initiation-list/program-initiation-list.component'

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'app-program-initiation-container',
  templateUrl: `./program-initiation.container.html`,
  imports: [ThemeContainerComponent, ProgramInitiationComponent, AsyncPipe],
  providers: [ProgramInitiationFlowService],
})
export class ProgramInitiationContainerComponent implements OnInit, OnDestroy {
  private store = inject<Store<AppState>>(Store)
  private actions$ = inject(Actions)
  private backendService = inject(BackendService)
  private initiationFlow = inject(ProgramInitiationFlowService)

  private destroy$ = new Subject<void>()
  private refreshList$ = new Subject<void>()
  private refreshMappings$ = new Subject<void>()
  private accountNameSearch$ = new Subject<string>()

  /** null until the account mappings load and the default Account filter is known */
  query$ = new BehaviorSubject<AccountOpportunityQuery | null>(null)
  /** Explicit opt-in to list every opportunity with no filters */
  showAll$ = new BehaviorSubject<boolean>(false)
  loading$ = new BehaviorSubject<boolean>(false)
  initiatingOppId$ = new BehaviorSubject<string | null>(null)

  /** null = idle: no filter selected and Show All not chosen, so nothing is fetched */
  page$: Observable<(MaybeData<AccountOpportunityPageDto> & MaybeError) | null>
  rows$: Observable<AccountOpportunityListItemDto[]>
  totalCount$: Observable<number>
  emptyMessage$: Observable<string>
  filterOptions$: Observable<AccountOpportunityFilterOptionsDto | null>
  accountNameSuggestions$: Observable<string[]>
  accountMappings$: Observable<ProgramInitiationAccountMappingDto[] | null>
  allCarriers$: ApiResponse<CarrierData[]>
  isSaving$: Observable<boolean>

  /** Carrier selected in SAGE has no SF account mapped, so there is no default Account filter */
  private currentCarrierUnmapped: string | null = null

  ngOnInit() {
    let currentCarrierId: number | null = null
    this.store
      .pipe(select(selectCurrentClientID), take(1))
      .subscribe((id) => (currentCarrierId = id ? Number(id) : null))

    this.accountMappings$ = this.refreshMappings$.pipe(
      startWith(undefined),
      switchMap(() => this.backendService.getProgramInitiationAccountMappings()),
      map((response) => response.data ?? null),
      takeUntil(this.destroy$),
      shareReplay({ bufferSize: 1, refCount: false }),
    )

    // Default the Account filter to the SF account mapped to the carrier selected in SAGE, as before
    this.accountMappings$
      .pipe(take(1), withLatestFrom(this.store.pipe(select(selectClientsByID))))
      .subscribe(([mappings, carriers]) => {
        const accountName =
          currentCarrierId !== null
            ? earliestMappedAccount(mappings ?? [], currentCarrierId)?.sfAccountName
            : undefined
        if (currentCarrierId !== null && !accountName) {
          this.currentCarrierUnmapped = carriers[currentCarrierId]?.name ?? 'the current carrier'
        }
        this.query$.next({
          ...EMPTY_ACCOUNT_OPPORTUNITY_FILTERS,
          accountName: accountName ?? '',
          sortBy: 'opportunityInceptionDate',
          sortDirection: 'desc',
          page: 1,
          pageSize: DEFAULT_ACCOUNT_OPPORTUNITY_PAGE_SIZE,
        })
      })

    const query$ = this.query$.pipe(
      filter((query): query is AccountOpportunityQuery => query !== null),
      distinctUntilChanged((a, b) => equals(a, b)),
    )

    // switchMap drops stale responses when filters change faster than the backend answers
    this.page$ = combineLatest([
      query$,
      this.showAll$.pipe(distinctUntilChanged()),
      this.refreshList$.pipe(startWith(undefined)),
    ]).pipe(
      switchMap(([query, showAll]) => {
        if (!showAll && !hasActiveFilters(query)) {
          this.loading$.next(false)
          return of(null)
        }
        this.loading$.next(true)
        return this.backendService
          .getAccountOpportunities(query)
          .pipe(tap(() => this.loading$.next(false)))
      }),
      shareReplay({ bufferSize: 1, refCount: true }),
    )
    this.rows$ = this.page$.pipe(map((page) => page?.data?.items ?? []))
    this.totalCount$ = this.page$.pipe(map((page) => page?.data?.totalCount ?? 0))

    this.emptyMessage$ = this.page$.pipe(
      map((page) => {
        if (!page) {
          const prompt =
            'Select a filter to find opportunities, or choose Show All to list every opportunity.'
          return this.currentCarrierUnmapped
            ? `No Salesforce account is mapped to ${this.currentCarrierUnmapped}. ${prompt}`
            : prompt
        }
        if (page.error) {
          return 'Unable to load opportunities.'
        }
        return 'No opportunities match the current filters.'
      }),
    )

    this.filterOptions$ = this.backendService
      .getProgramInitiationFilterOptions(null)
      .pipe(map((response) => response.data ?? null))

    this.accountNameSuggestions$ = this.accountNameSearch$.pipe(
      switchMap((term) =>
        term.length >= MIN_SEARCH_LENGTH
          ? this.backendService.searchProgramInitiationAccountNames(term, null)
          : of({ data: [] as string[] }),
      ),
      map((response) => response.data ?? []),
    )

    this.allCarriers$ = this.backendService.getCarriers()
    this.isSaving$ = this.store.pipe(select(selectIsSaving))

    // Re-fetch the current page once an initiation (or mapping change) lands so rows show as initiated
    this.actions$
      .pipe(
        ofType(
          fromProgramInitiation.createNewProgramFromOpportunitySuccess,
          fromProgramInitiation.createNewYearProgramFromOpportunitySuccess,
          // A new carrier may have been created and mapped before the year/program step failed
          fromProgramInitiation.createNewYearProgramFromOpportunityFailure,
          fromProgramInitiation.updateSFAccountCarrierMappingSuccess,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.refreshList$.next())

    this.actions$
      .pipe(
        ofType(
          fromProgramInitiation.createNewCarrierYearProgramFromOpportunitySuccess,
          fromProgramInitiation.updateSFAccountCarrierMappingSuccess,
        ),
        takeUntil(this.destroy$),
      )
      .subscribe(() => this.refreshMappings$.next())
  }

  ngOnDestroy() {
    this.destroy$.next()
    this.destroy$.complete()
  }

  onFiltersChange(filters: AccountOpportunityFilters): void {
    // Picking any filter leaves Show All mode
    if (hasActiveFilters(filters)) {
      this.showAll$.next(false)
    }
    this.updateQuery({ ...filters, page: 1 })
  }

  onShowAllChange(showAll: boolean): void {
    // Show All and Clear both reset the filters; apply that now rather than waiting for the
    // filter bar's debounced filtersChange, so the old filters are never fetched with showAll
    this.updateQuery({ ...EMPTY_ACCOUNT_OPPORTUNITY_FILTERS, page: 1 })
    this.showAll$.next(showAll)
  }

  onSortChange(sort: ProgramInitiationSortChange): void {
    this.updateQuery({ ...sort, page: 1 })
  }

  onPageChange(page: ProgramInitiationPageChange): void {
    this.updateQuery(page)
  }

  onAccountNameSearch(term: string): void {
    this.accountNameSearch$.next(term)
  }

  onInitiate(opportunity: AccountOpportunityListItemDto): void {
    this.initiatingOppId$.next(opportunity.oppId)
    this.initiationFlow.initiate(opportunity)
  }

  onUpdateSFMapping(accountMapping: AccountMappingData): void {
    this.store.dispatch(fromProgramInitiation.updateSFAccountCarrierMapping({ accountMapping }))
  }

  private updateQuery(changes: Partial<AccountOpportunityQuery>): void {
    const query = this.query$.value
    if (query) {
      this.query$.next({ ...query, ...changes })
    }
  }
}
